<template>
  <article class="view-settings-team">
    <template v-if="team && (membership || organizationRole?.canManageTeams)">
      <header class="view-settings-team__header">
        <h1>{{ team.name }}</h1>
        <button
          v-if="canEditTeam"
          class="button-icon"
          type="button"
          @click="startEditing"
        >
          <lucide-icon name="pencil" />
        </button>
      </header>

      <formatted-text :value="team.description" />

      <form
        v-if="data.editing && canEditTeam"
        class="column"
        @submit.prevent="saveForm"
      >
        <input-text v-model:value="data.newName" :label="$t('labels.name')" />
        <input-text-editor
          v-model:value="data.newDescription"
          :label="$t('labels.description')"
        />
        <div class="line_flex">
          <button class="button" type="submit">{{ $t("labels.save") }}</button>
          <button
            class="button button_secondary"
            type="button"
            @click="data.editing = false"
          >
            {{ $t("labels.abort") }}
          </button>
        </div>
      </form>

      <!-- MEMBERS -->

      <h3 class="subtitle mt-6 mb-5">{{ $t("headings.members") }}</h3>

      <member-list
        :members="members"
        :roles="roles"
        :can-manage-members="canManageMembers"
        :can-see-emails="teamRole?.canSeeEmails"
        :user-membership="membership"
        :assignable-role-codes="assignableRoleCodes"
        :max-members="null"
        @update="tryUpdatingTeamMember"
        @delete="tryRemovingMember"
      />
      <div class="line_flex mt-4">
        <button
          v-if="canManageMembers && membership == null"
          type="button"
          class="button"
          @click="tryJoiningTeam()"
        >
          {{ $t("actions.joinTeam") }}
        </button>
        <button
          v-if="canManageMembers"
          type="button"
          class="button"
          @click="data.isMemberInvitationPopupOpen = true"
        >
          {{ $t("actions.inviteTeamMembers") }}
        </button>
      </div>
      <member-invitation-popup
        :visible="data.isMemberInvitationPopupOpen"
        :title="$t('actions.addMoreMembers')"
        :existing-emails="memberEmails"
        :invite-function="sendInvitations"
        :autocomplete-options="organizationMembersAutocompleteOptions"
        :can-set-valid-until="false"
        @close="data.isMemberInvitationPopupOpen = false"
      >
        <p>
          {{ $t("texts.teamMemberInvitationMessage") }}
        </p>
      </member-invitation-popup>

      <template v-if="teamRole != null">
        <hr class="mt-7" />
        <button
          class="button button_secondary"
          type="button"
          @click="tryLeavingTeam()"
        >
          {{ $t("actions.leaveTeam") }}
        </button>
      </template>

      <template v-if="canEditTeam">
        <hr class="mt-7" />
        <h3 class="subtitle">{{ $t("headings.teamSettings") }}</h3>
        <input-checkbox
          :value="team.membersCanSeeEmails"
          :label="$t('labels.membersCanSeeEmails')"
          @update:value="updateMembersCanSeeEmails($event)"
        />

        <hr class="mt-7" />
        <button
          class="button button_secondary"
          type="button"
          @click="tryDestroyingTeam()"
        >
          {{ $t("actions.destroyTeam") }}
        </button>
      </template>
    </template>
    <template v-else>
      <error-page-content
        error-code="team_not_found"
        :status="404"
        :title="$t('errors.teamNotFound')"
      />
    </template>
  </article>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { notNull } from "@/helpers/utility/Type";

import InputText from "@core/input/InputText.vue";
import InputTextEditor from "@core/input/TextEditor.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import MemberList from "../members/MemberList.vue";
import MemberInvitationPopup from "../members/MemberInvitationPopup.vue";
import ErrorPageContent from "@core/errors/ErrorPageContent.vue";

import { useOrganizations } from "@manager/organizations/organizations.store";
import { useUser } from "@/api/user.store";
import { useWorkspaces } from "@/api/workspaces.store";
import { showConfirm, showError } from "@core/ui/alert/alert";

import type { OrganizationWorkspace, TeamRole } from "@/api/Workspace";

const props = defineProps<{
  organizationSlug: string;
  teamSlug: string;
}>();

const { t } = useI18n();
const router = useRouter();

const { user } = useUser();
const {
  getOrganization,
  currentOrganization,
  currentOrganizationRoles,
  updateTeam,
  destroyTeam
} = useOrganizations(props, { user });
const {
  slugWorkspace,
  inviteTeamMembers,
  updateTeamMember,
  removeTeamMember,
  leaveTeam,
  joinTeam
} = useWorkspaces(props);

getOrganization(props.organizationSlug);

const organization = currentOrganization;
const organizationRoles = currentOrganizationRoles;
const team = computed(() => slugWorkspace.value as OrganizationWorkspace);

const data = reactive({
  editing: false,
  newName: "",
  newDescription: "" as string | undefined,
  isMemberInvitationPopupOpen: false
});

const members = computed(() => {
  return team.value?.members ?? [];
});

const memberEmails = computed(() => {
  return members.value.map((m) => m.email).filter(notNull);
});

const roles = computed(() => {
  const roles: Record<string, TeamRole> = {};
  const rolesArray = team.value?.roles ?? [];
  for (const role of rolesArray) {
    roles[role.roleCode] = role;
  }
  return roles;
});

const assignableRoleCodes = computed(() => {
  return Object.values(roles.value).map((r) => r.roleCode);
});

const membership = computed(() => {
  return team.value?.membership;
});

const teamRole = computed(() => {
  if (!membership.value) return null;
  return roles.value[membership.value.roleCode];
});

const organizationRole = computed(() => {
  const organizationMembership = organization.value?.membership;
  if (organizationMembership == null) return null;
  return organizationRoles.value[organizationMembership.roleCode];
});

const canManageMembers = computed(() => {
  return (
    organizationRole.value?.canManageTeams || teamRole.value?.canManageMembers
  );
});

const canEditTeam = computed(() => {
  return (
    organizationRole.value?.canManageTeams || teamRole.value?.canUpdateWorkspace
  );
});

const organizationMembersAutocompleteOptions = computed(() => {
  const organizationMembers = organization.value?.members ?? [];
  return organizationMembers.map((m) => ({
    value: m.email,
    label: `${m?.name || ""}`
  }));
});

function startEditing() {
  if (data.editing == false) {
    data.newName = team.value.name;
    data.newDescription = team.value.description;
    data.editing = true;
  } else {
    data.editing = false;
  }
}

async function saveForm() {
  if (!organization.value) {
    showError("No organization.");
    return;
  }

  await updateTeam(organization.value, {
    slug: team.value.slug,
    name: data.newName,
    description: data.newDescription
  });

  data.editing = false;
}

async function updateMembersCanSeeEmails(value: boolean) {
  if (!organization.value) {
    showError("No organization.");
    return;
  }

  await updateTeam(organization.value, {
    slug: team.value.slug,
    membersCanSeeEmails: value
  });
}

async function sendInvitations(
  entries: {
    user: { email: string };
    validUntil?: string | undefined;
    roleCode?: string | undefined;
  }[]
) {
  if (!team.value) {
    return;
  }

  return await inviteTeamMembers(team.value, entries);
}

async function tryUpdatingTeamMember(memberData: {
  id: number;
  roleCode: string;
}) {
  if (!user.value) {
    showError("No user.");
    return;
  }

  // make sure to ask the user again if they are
  // trying to remove their own admin rights...
  if (memberData.id === user.value.id && memberData.roleCode !== "ADMIN") {
    if ((await showConfirm(t("questions.removeOwnAdminRights"))) === false) {
      return;
    }
  }

  if (!team.value) {
    showError("No team.");
    return;
  }

  // send the data
  try {
    await updateTeamMember(team.value, memberData);
  } catch (error) {
    showError(error as Error);
  }
}

async function tryRemovingMember(member: { id: number }) {
  if (!team.value) {
    showError("No team.");
    return;
  }

  try {
    await removeTeamMember(team.value, member);
  } catch (error) {
    showError(error as Error);
  }
}

async function tryJoiningTeam() {
  if (!team.value) {
    showError("No team.");
    return;
  }

  try {
    await joinTeam(team.value);
  } catch (error) {
    console.log(error);
  }
}

async function tryDestroyingTeam() {
  if (!team.value) {
    showError("No team.");
    return;
  }

  if (
    await showConfirm(
      t("questions.confirmDeleteTeam", { name: team.value.name }),
      t("actions.deletePermanently")
    )
  ) {
    try {
      await destroyTeam(team.value);
      
      router.push({
        name: "settings-organization",
        params: {
          organizationSlug: props.organizationSlug
        }
      });
    } catch (error) {
      showError(error as Error);
    }
  }
}

async function tryLeavingTeam() {
  if (!team.value) {
    showError("No team.");
    return;
  }

  if (
    await showConfirm(
      t("questions.confirmLeaveTeam", { name: team.value.name }),
      t("actions.leaveTeam")
    )
  ) {
    try {
      await leaveTeam(team.value);

      router.push({
        name: "settings-organization",
        params: {
          organizationSlug: props.organizationSlug
        }
      });
    } catch (error) {
      showError(error as Error);
    }
  }

}
</script>

<style lang="scss">
.view-settings-team {
  &__header {
    display: flex;

    h1 {
      min-width: 0;
      word-break: break-word;
    }
  }
}
</style>
